import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { Seo } from '../components/Seo.jsx';
import { Alert, Spinner } from '../components/ui.jsx';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refresh } = useAuth();
  const [state, setState] = useState(token ? 'working' : 'failed');
  const [message, setMessage] = useState(token ? '' : 'This link is incomplete.');
  // The token is single-use. React StrictMode runs effects twice in development,
  // and the second call would fail and overwrite the success.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post('/auth/verify', { token })
      .then(async () => {
        setState('done');
        // Always re-read the session. This effect runs on first render, before
        // the initial session check has finished, so `user` is usually still
        // null here — gating on it left a signed-in person marked unconfirmed
        // until they reloaded.
        await refresh().catch(() => {});
      })
      .catch((err) => {
        setState('failed');
        // A mangled link fails the server's token-shape check; say what a
        // dead link always says rather than a bare "Validation failed".
        setMessage(
          err.status === 400 && err.details && 'token' in err.details
            ? 'That confirmation link is invalid or has expired.'
            : err.message,
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      <Seo title="Confirm your email" noindex />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="text-2xs font-semibold uppercase tracking-widest text-accent">
          Affily
        </Link>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Confirm your email</h1>
        <div className="mt-6">
          {state === 'working' && (
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Spinner className="size-4" /> Confirming…
            </p>
          )}
          {state === 'done' && (
            <>
              <Alert tone="positive">Your email address is confirmed. Thank you.</Alert>
              <Link to="/dashboard" className="mt-4 inline-block text-sm font-medium text-accent hover:underline">
                Go to your dashboard →
              </Link>
            </>
          )}
          {state === 'failed' && (
            <>
              <Alert>{message}</Alert>
              <p className="mt-3 text-sm text-ink-muted">
                Sign in and use “Resend” in the banner on your dashboard to get a fresh link.
              </p>
              <Link to="/login" className="mt-2 inline-block text-sm font-medium text-accent hover:underline">
                Sign in →
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
